/**
 * @file Task detail field renderer and task-specific detail controls.
 */

import { appData } from '../../app/state/state.js';
import { renderAuditTrail, renderLongTextDetailSection } from '../../shared/details/detailSections.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import {
    createDateControl,
} from '../../shared/ui/controls/dateControl.js';
import {
    getRegisteredControlValues,
    updateControlValue,
} from '../../shared/ui/controls/controlCore.js';
import {
    createPrioritySelectPreset,
    createStatusSelectPreset,
    createTypeSelectPreset,
    createUserSelectPreset,
} from '../../shared/ui/controls/controlPresets.js';
import {
    createSelectControl,
} from '../../shared/ui/controls/selectControl.js';
import {
    findItem,
    getCollectionName,
    getItemDisplayName,
    getItemType,
} from "../../shared/utils/itemLookup.js";
import {
    getAssignableUsersWithPermission,
    getSortedUsers,
    getUserFullDisplayName,
} from "../../shared/utils/userDisplay.js";
import {
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    DETAIL_TITLE_INPUT_CLASSES,
    FIELD_WRAPPER_CLASSES,
    REQUIRED_MARK_HTML,
    getFieldLabelHtml,
    getNativeFieldWrapperHtml,
} from '../../shared/forms/nativeFieldStyles.js';

const TASK_RECURRENCE_WEEKDAY_OPTIONS = [
    { value: 'same_day', label: 'On the same day' },
    { value: 'monday', label: 'On Monday' },
    { value: 'tuesday', label: 'On Tuesday' },
    { value: 'wednesday', label: 'On Wednesday' },
    { value: 'thursday', label: 'On Thursday' },
    { value: 'friday', label: 'On Friday' },
    { value: 'saturday', label: 'On Saturday' },
    { value: 'sunday', label: 'On Sunday' },
];

const TASK_RECURRENCE_MONTHLY_OPTIONS = [
    { value: 'same_date', label: 'On the same date' },
    { value: 'first_day', label: 'On the first day of the month' },
    { value: 'last_day', label: 'On the last day of the month' },
];

const TASK_RECURRENCE_FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
];

const TASK_RECURRENCE_ANCHOR_OPTIONS = [
    { value: 'dueDate', label: 'Due date' },
    { value: 'completionDate', label: 'Date completed' },
];

function scalarControlRootHtml(fieldName) {
    return `<div class="min-w-0" data-detail-scalar-control="${fieldName}"></div>`;
}

function attrValue(value = '') {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function getTaskAssignmentCollectionName(item) {
    const parent = item?.parentId ? findItem(item.parentId) : null;
    const parentType = item?.parentType || (parent?.id ? getItemType(parent.id) : null);
    return parentType ? getCollectionName(parentType) : null;
}

/**
 * Builds Task assigned-user picker options and team metadata.
 * @param {object} item Task item data.
 * @returns {object} Assignment picker context.
 */
export function getTaskAssignedUserPickerContext(item) {
    const collectionName = getTaskAssignmentCollectionName(item);
    const allowedUsers = getSortedUsers(getAssignableUsersWithPermission(collectionName));
    const allowedUserIds = allowedUsers.map((user) => user.id);
    const allowedUserMap = new Map(allowedUsers.map((user) => [user.id, user]));
    const allUsersMap = new Map((appData.users || []).map((user) => [user.id, user]));
    const selectedValues = Array.isArray(item?.assigned_users) ? item.assigned_users : [];
    const optionsById = new Map();

    allowedUsers.forEach((user, index) => {
        optionsById.set(String(user.id), {
            value: String(user.id),
            label: getUserFullDisplayName(user),
            inactive: user?.isActive === false,
            group: 'Users',
            order: index,
            source: user,
        });
    });

    selectedValues.forEach((userId) => {
        const value = String(userId);
        if (optionsById.has(value)) {return;}
        const user = allUsersMap.get(value) || { id: value, name: value };
        optionsById.set(value, {
            value,
            label: getUserFullDisplayName(user),
            inactive: user?.isActive === false,
            selectedOnly: true,
            unavailableLabel: allowedUserMap.has(value) ? '' : 'no access',
            group: 'Users',
            order: 10000,
            source: user,
        });
    });

    const visibleTeams = (appData.teams || [])
        .filter(team => team?.visible !== false)
        .map(team => {
            const eligibleMemberIds = (team.members || [])
                .filter(uid => allowedUserIds.includes(uid));
            return { ...team, eligibleMemberIds };
        })
        .filter(team => team.eligibleMemberIds.length > 0)
        .sort((a, b) => {
            const orderA = Number.isFinite(Number(a.order)) ? Number(a.order) : 999;
            const orderB = Number.isFinite(Number(b.order)) ? Number(b.order) : 999;
            if (orderA !== orderB) {return orderA - orderB;}
            return (a.name || '').localeCompare(b.name || '');
        });

    visibleTeams.forEach((team) => {
        optionsById.set(`team:${team.id}`, {
            value: `team:${team.id}`,
            label: `${team.name || 'Unnamed Team'} (${team.eligibleMemberIds.length} users)`,
            group: 'Teams',
            order: Number.isFinite(Number(team.order)) ? Number(team.order) : 9999,
            source: team,
        });
    });

    return {
        allUsersMap,
        allowedUserMap,
        allowedUsers,
        collectionName,
        options: Array.from(optionsById.values()),
        selectedValues,
        visibleTeams,
    };
}

/**
 * Generates HTML for editable Task detail fields.
 * @param {object} item Task item data.
 * @returns {string} Task detail fields HTML.
 */
export function getTaskDetailFields(item) {
    const val = (field, fallback = '') => item?.[field] ?? fallback;
    const recurrence = getTaskDetailRecurrenceState(item);
    const parent = item?.parentId ? findItem(item.parentId) : null;
    const parentType = item?.parentType || (parent?.id ? getItemType(parent.id) : null);
    const typeDisplay = parentType ?
        parentType.charAt(0).toUpperCase() + parentType.slice(1) :
        'Linked item';
    const parentIsUnavailable = Boolean(item?.parentId) && !parent;

    let parentLabelHtml = '';
    if (parent) {
        parentLabelHtml = `<button type="button" class="task-parent-header-link mb-0.5 inline cursor-pointer border-0 bg-transparent p-0 text-left text-[15px] font-medium text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus:text-[var(--dfc-brand-primary)]" data-parent-link="${parent.id}">${sanitizeHTML(typeDisplay)}: ${sanitizeHTML(getItemDisplayName(parent, 'Linked item'))}</button>`;
    } else if (parentIsUnavailable) {
        parentLabelHtml = `<span class="mb-0.5 inline-block text-[15px] font-medium text-gray-400">${sanitizeHTML(typeDisplay)} unavailable</span>`;
    }

    return `
        <div class="bg-white pt-6 px-6 pb-3 rounded-lg shadow-md mb-6">
            <div class="mb-4 pb-2">
                ${parentLabelHtml}
                <textarea name="title" class="${DETAIL_TITLE_INPUT_CLASSES} resize-none overflow-hidden" placeholder="Task Title" aria-label="Task Title" autocomplete="off" rows="1" data-auto-grow="true" required>${attrValue(val('title'))}</textarea>
            </div>
            ${recurrence.isActive ? `
                <div id="task-detail-recurrence-shell" class="group -mt-2">
                    <input type="hidden" name="recurrence_enabled" value="true">
                    <div id="task-detail-recurrence-statement" class="inline-flex max-w-full items-center gap-1.5 text-sm text-green-700 dark:text-green-300">
                        <span class="material-symbols-outlined text-[16px] leading-none text-green-600 dark:text-green-300">repeat</span>
                        <span id="task-detail-recurrence-text" class="truncate">${getTaskDetailRecurrenceStatement(recurrence)}</span>
                        <div class="ml-0.5 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                            <button type="button" id="task-detail-recurrence-edit-btn" class="inline-flex rounded-full p-0.5 text-gray-500 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none" title="Edit recurrence" aria-label="Edit recurrence" aria-expanded="false">
                                <span class="material-symbols-outlined text-base leading-none">edit</span>
                            </button>
                            <button type="button" id="task-detail-recurrence-delete-btn" class="p-0.5 text-gray-400 transition-colors hover:text-red-500 focus:outline-none" title="Delete recurrence" aria-label="Delete recurrence">
                                <span class="material-symbols-outlined text-base leading-none">delete</span>
                            </button>
                        </div>
                    </div>
                    <div id="task-detail-recurrence-editor" class="mt-1.5 hidden rounded-lg bg-green-50 px-4 py-3 dark:bg-green-500/10">
                        <h4 class="mb-3 text-sm font-semibold text-green-700 dark:text-green-300">Recurring Task Schedule</h4>
                        <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-4">
                            <div class="${FIELD_WRAPPER_CLASSES}">
                                ${getFieldLabelHtml('Repeat', true)}
                                <div data-task-detail-recurrence-control="frequency"></div>
                            </div>
                            <div id="task-detail-recurrence-modifier-wrap" class="${FIELD_WRAPPER_CLASSES} ${showTaskDetailRecurrenceModifier(recurrence.frequency) ? '' : 'hidden'}">
                                <label id="task-detail-recurrence-modifier-label" class="block text-base text-gray-500 sm:text-sm">${getTaskDetailRecurrenceModifierLabel(recurrence.frequency)}${REQUIRED_MARK_HTML}</label>
                                <div data-task-detail-recurrence-control="modifier"></div>
                            </div>
                            <div id="task-detail-recurrence-anchor-wrap" class="${FIELD_WRAPPER_CLASSES} ${showTaskDetailRecurrenceAnchor(recurrence.frequency, recurrence.modifier) ? '' : 'hidden'}">
                                ${getFieldLabelHtml('Calculate Next From', true)}
                                <div data-task-detail-recurrence-control="anchor"></div>
                            </div>
                            <div class="flex items-end">
                                <button type="button" id="task-detail-recurrence-save-btn" class="inline-flex h-11 items-center justify-center rounded-md bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-gray-900 transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none sm:h-[30px]">Done</button>
                            </div>
                        </div>
                    </div>
                </div>
            ` : ''}
            <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6 text-sm mt-4">
                ${getNativeFieldWrapperHtml('Task Type(s)', scalarControlRootHtml('types'), { className: 'md:col-span-2' })}
                ${getNativeFieldWrapperHtml('Assigned to', scalarControlRootHtml('assigned_users'), { className: 'md:col-span-2' })}

                ${getNativeFieldWrapperHtml('Task Owner', scalarControlRootHtml('owner'))}
                ${getNativeFieldWrapperHtml('Due Date', scalarControlRootHtml('due_date'))}
                ${getNativeFieldWrapperHtml('Urgency', scalarControlRootHtml('priority'))}
                ${getNativeFieldWrapperHtml('Status', scalarControlRootHtml('status'))}

                ${renderLongTextDetailSection({
        fieldName: 'description',
        label: 'Details',
        value: val('description'),
        wrapperClass: 'md:col-span-4 text-sm',
    })}
            </div>

            ${renderAuditTrail(item)}
        </div>
    `;
}

/**
 * Mounts registered type controls for Task details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Task item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupTaskDetailTypeControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    const root = form?.querySelector('[data-detail-scalar-control="types"]');
    if (!root) {return controls;}

    controls.types = createTypeSelectPreset({
        root,
        appData,
        itemType: 'task',
        value: Array.isArray(item?.types) ? item.types : [],
        searchable: true,
    });

    return controls;
}

/**
 * Mounts registered scalar controls for Task details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Task item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupTaskDetailScalarControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    if (!form) {return controls;}

    const getRoot = (fieldName) => form.querySelector(
        `[data-detail-scalar-control="${fieldName}"]`,
    );
    const value = (fieldName) => item?.[fieldName] ?? '';

    const dueDateRoot = getRoot('due_date');
    if (dueDateRoot) {
        controls.due_date = createDateControl({
            root: dueDateRoot,
            name: 'due_date',
            type: 'date',
            value: value('due_date'),
        });
    }

    const priorityRoot = getRoot('priority');
    if (priorityRoot) {
        controls.priority = createPrioritySelectPreset({
            root: priorityRoot,
            appData,
            value: value('priority'),
            clearable: false,
        });
    }

    const statusRoot = getRoot('status');
    if (statusRoot) {
        controls.status = createStatusSelectPreset({
            root: statusRoot,
            appData,
            itemType: 'task',
            value: value('status'),
            clearable: false,
        });
    }

    return controls;
}

/**
 * Mounts registered owner and assigned-user controls for Task details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Task item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupTaskDetailPeopleControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    if (!form) {return controls;}

    const getRoot = (fieldName) => form.querySelector(
        `[data-detail-scalar-control="${fieldName}"]`,
    );
    const assignmentContext = getTaskAssignedUserPickerContext(item);

    const assignedRoot = getRoot('assigned_users');
    if (assignedRoot) {
        controls.assigned_users = createSelectControl({
            root: assignedRoot,
            name: 'assigned_users',
            value: assignmentContext.selectedValues,
            options: assignmentContext.options,
            multiple: true,
            searchable: true,
            searchThreshold: 7,
            placeholder: '+ Add assigned users',
        });
    }

    const ownerRoot = getRoot('owner');
    if (ownerRoot) {
        controls.owner = createUserSelectPreset({
            root: ownerRoot,
            appData,
            name: 'owner',
            collectionName: assignmentContext.collectionName,
            value: item?.owner || '',
            clearable: false,
            searchable: true,
        });
    }

    return controls;
}

function getTaskDetailRecurrenceState(item = {}) {
    const recurrence = item?.recurrence || {};
    const frequency = recurrence.frequency || '';

    return {
        isActive: !!frequency,
        frequency,
        modifier: recurrence.modifier || '',
        anchor: recurrence.anchor || 'dueDate',
    };
}

function getTaskDetailWeekdayValue(dateString) {
    if (!dateString) {return 'monday';}
    const parsedDate = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {return 'monday';}

    const weekdayMap = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
    ];

    return weekdayMap[parsedDate.getDay()] || 'monday';
}

function showTaskDetailRecurrenceModifier(frequency) {
    return frequency === 'weekly' || frequency === 'monthly';
}

function showTaskDetailRecurrenceAnchor(frequency, modifier) {
    if (frequency === 'daily' || frequency === 'yearly') {return true;}
    if (frequency === 'weekly') {return modifier === 'same_day';}
    if (frequency === 'monthly') {return modifier === 'same_date';}
    return false;
}

function getTaskDetailRecurrenceModifierLabel(frequency) {
    if (frequency === 'weekly') {return 'Repeat on';}
    if (frequency === 'monthly') {return 'Repeat on';}
    return 'Rule';
}

function getTaskDetailRecurrenceModifierOptions(frequency, dueDate) {
    if (frequency === 'weekly') {
        const weekday = getTaskDetailWeekdayValue(dueDate);
        return TASK_RECURRENCE_WEEKDAY_OPTIONS.map(option => ({
            ...option,
            isDefault: option.value === weekday,
        }));
    }

    if (frequency === 'monthly') {
        return TASK_RECURRENCE_MONTHLY_OPTIONS.map(option => ({
            ...option,
            isDefault: option.value === 'same_date',
        }));
    }

    return [];
}

function getTaskDetailRecurrenceStatement(recurrence) {
    if (!recurrence?.isActive) {return '';}

    const frequencyLabels = {
        daily: 'daily',
        weekly: 'weekly',
        monthly: 'monthly',
        yearly: 'yearly',
    };

    const modifierLabels = {
        monday: 'on Monday',
        tuesday: 'on Tuesday',
        wednesday: 'on Wednesday',
        thursday: 'on Thursday',
        friday: 'on Friday',
        saturday: 'on Saturday',
        sunday: 'on Sunday',
        same_day: 'on the same day',
        same_date: 'on the same date',
        first_day: 'on the first day',
        last_day: 'on the last day',
    };

    const frequency = frequencyLabels[recurrence.frequency] || 'daily';
    const modifier = recurrence.modifier ?
        ` ${modifierLabels[recurrence.modifier] || ''}` :
        '';
    const shouldIncludeAnchor = showTaskDetailRecurrenceAnchor(
        recurrence.frequency,
        recurrence.modifier,
    );
    const anchor = recurrence.anchor === 'completionDate' ?
        'date completed' :
        'due date';
    const anchorText = shouldIncludeAnchor ?
        ` calculated from ${anchor}` :
        '';
    return `Task repeats ${frequency}${modifier}${anchorText}.`;
}

/**
 * Wires task recurrence detail controls.
 * @param {HTMLElement} detailsViewContainer Detail view container.
 * @param {object} item Task item.
 * @param {Function} enableSaveButtons Dirty-state save button callback.
 * @param {object} uiDraft Transient UI draft state.
 */
export function setupTaskDetailRecurrenceControls(
    detailsViewContainer,
    item,
    enableSaveButtons,
    uiDraft = {},
) {
    const recurrenceShell = detailsViewContainer.querySelector(
        '#task-detail-recurrence-shell',
    );
    const recurrenceStatement = detailsViewContainer.querySelector(
        '#task-detail-recurrence-text',
    );
    const recurrenceEditor = detailsViewContainer.querySelector(
        '#task-detail-recurrence-editor',
    );
    const editBtn = detailsViewContainer.querySelector(
        '#task-detail-recurrence-edit-btn',
    );
    const deleteBtn = detailsViewContainer.querySelector(
        '#task-detail-recurrence-delete-btn',
    );
    const saveBtn = detailsViewContainer.querySelector(
        '#task-detail-recurrence-save-btn',
    );
    const recurrenceEnabledInput = detailsViewContainer.querySelector(
        'input[name="recurrence_enabled"]',
    );
    const frequencyRoot = detailsViewContainer.querySelector(
        '[data-task-detail-recurrence-control="frequency"]',
    );
    const modifierRoot = detailsViewContainer.querySelector(
        '[data-task-detail-recurrence-control="modifier"]',
    );
    const modifierWrap = detailsViewContainer.querySelector(
        '#task-detail-recurrence-modifier-wrap',
    );
    const modifierLabel = detailsViewContainer.querySelector(
        '#task-detail-recurrence-modifier-label',
    );
    const anchorWrap = detailsViewContainer.querySelector(
        '#task-detail-recurrence-anchor-wrap',
    );
    const anchorRoot = detailsViewContainer.querySelector(
        '[data-task-detail-recurrence-control="anchor"]',
    );
    const dueDateRoot = detailsViewContainer.querySelector(
        '[data-detail-scalar-control="due_date"]',
    );
    const detailsForm = detailsViewContainer.querySelector('#details-form');

    if (!recurrenceShell || !recurrenceStatement || !recurrenceEditor ||
        !editBtn || !deleteBtn || !saveBtn || !recurrenceEnabledInput ||
        !frequencyRoot || !modifierRoot || !modifierWrap ||
        !modifierLabel || !anchorWrap || !anchorRoot || !dueDateRoot ||
        !detailsForm) {
        return;
    }

    const initialState = getTaskDetailRecurrenceState(item);
    const initialDueDate = item?.due_date || '';
    let warningLock = false;
    let isEditing = false;

    const frequencyControl = createSelectControl({
        root: frequencyRoot,
        name: 'recurrence_frequency',
        value: initialState.frequency,
        options: TASK_RECURRENCE_FREQUENCY_OPTIONS,
        placeholder: 'Select',
        clearable: false,
        required: true,
        onChange: () => syncModifierOptions(),
    });
    const modifierControl = createSelectControl({
        root: modifierRoot,
        name: 'recurrence_modifier',
        value: initialState.modifier,
        options: getTaskDetailRecurrenceModifierOptions(
            initialState.frequency,
            initialDueDate,
        ),
        placeholder: 'Select',
        clearable: false,
        disabled: !showTaskDetailRecurrenceModifier(initialState.frequency),
        required: showTaskDetailRecurrenceModifier(initialState.frequency),
        onChange: () => syncAnchorVisibility(),
    });
    const anchorControl = createSelectControl({
        root: anchorRoot,
        name: 'recurrence_anchor',
        value: initialState.anchor,
        options: TASK_RECURRENCE_ANCHOR_OPTIONS,
        placeholder: 'Select',
        clearable: false,
        disabled: !showTaskDetailRecurrenceAnchor(
            initialState.frequency,
            initialState.modifier,
        ),
        required: showTaskDetailRecurrenceAnchor(
            initialState.frequency,
            initialState.modifier,
        ),
    });

    const getDueDateControlId = () => {
        return getRegisteredControlValues(detailsForm).sources.due_date || '';
    };
    const getDueDateValue = () => {
        const extraction = getRegisteredControlValues(detailsForm);
        return extraction.values.due_date ?? '';
    };
    const setDueDateValue = (value) => {
        const controlId = getDueDateControlId();
        if (controlId) {
            updateControlValue(controlId, value, { silent: true });
        }
    };
    const setControlValue = (control, value) => {
        control.setValue(value, { silent: true });
    };
    const updateControl = (control, updates) => {
        control.update(updates);
    };
    const hasOptionValue = (options, value) => {
        return options.some((option) => String(option.value) === String(value));
    };

    const getCurrentRecurrenceState = () => ({
        isActive: recurrenceEnabledInput.value === 'true',
        frequency: frequencyControl.value || '',
        modifier: modifierControl.value || '',
        anchor: anchorControl.value || 'dueDate',
    });

    const syncAnchorVisibility = () => {
        const frequency = frequencyControl.value;
        const modifier = modifierControl.value;
        const shouldShowAnchor = recurrenceEnabledInput.value === 'true' &&
            showTaskDetailRecurrenceAnchor(frequency, modifier);
        anchorWrap.classList.toggle('hidden', !shouldShowAnchor);
        updateControl(anchorControl, {
            disabled: !shouldShowAnchor,
            required: shouldShowAnchor,
        });
        if (!shouldShowAnchor && anchorControl.value) {
            setControlValue(anchorControl, '');
        }
    };

    function syncModifierOptions() {
        const frequency = frequencyControl.value;
        const shouldShowModifier = recurrenceEnabledInput.value === 'true' &&
            showTaskDetailRecurrenceModifier(frequency);
        const modifierOptions = getTaskDetailRecurrenceModifierOptions(
            frequency,
            getDueDateValue() || initialDueDate,
        );

        modifierWrap.classList.toggle('hidden', !shouldShowModifier);
        modifierLabel.innerHTML = `${getTaskDetailRecurrenceModifierLabel(frequency)}${REQUIRED_MARK_HTML}`;
        updateControl(modifierControl, {
            disabled: !shouldShowModifier,
            options: modifierOptions,
            required: shouldShowModifier,
        });

        if (!shouldShowModifier) {
            if (modifierControl.value) {setControlValue(modifierControl, '');}
            syncAnchorVisibility();
            return;
        }

        if (!hasOptionValue(modifierOptions, modifierControl.value)) {
            setControlValue(modifierControl, '');
        }
        syncAnchorVisibility();
    }

    const updateStatement = () => {
        recurrenceStatement.textContent = getTaskDetailRecurrenceStatement(
            getCurrentRecurrenceState(),
        );
    };

    const setEditingState = (nextEditing) => {
        isEditing = nextEditing;
        recurrenceEditor.classList.toggle('hidden', !nextEditing);
        editBtn.setAttribute('aria-expanded', String(nextEditing));
        editBtn.style.color = nextEditing ? 'var(--dfc-brand-accent)' : '';
    };

    editBtn.addEventListener('click', () => {
        setEditingState(!isEditing);
    });

    frequencyRoot.addEventListener('control:valuechange', () => {
        syncModifierOptions();
    });

    modifierRoot.addEventListener('control:valuechange', () => {
        syncAnchorVisibility();
    });

    saveBtn.addEventListener('click', async () => {
        const recurrenceFrequency = frequencyControl.value || null;
        let recurrenceAnchor = anchorControl.value || null;
        let recurrenceModifier = modifierControl.value || null;

        if (!recurrenceFrequency) {
            openConfirmationModal(
                'Repeat Rule Required',
                'Choose how often this task repeats.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        if (showTaskDetailRecurrenceAnchor(
            recurrenceFrequency,
            recurrenceModifier,
        ) && !recurrenceAnchor) {
            openConfirmationModal(
                'Calculation Rule Required',
                'Choose what the recurrence should be calculated from.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        if ((recurrenceFrequency === 'weekly' ||
            recurrenceFrequency === 'monthly') &&
            !recurrenceModifier) {
            openConfirmationModal(
                'Repeat Rule Required',
                'Choose the recurrence rule for this task.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        if (recurrenceFrequency !== 'weekly' &&
            recurrenceFrequency !== 'monthly') {
            recurrenceModifier = null;
        }

        if (!showTaskDetailRecurrenceAnchor(
            recurrenceFrequency,
            recurrenceModifier,
        )) {
            recurrenceAnchor = null;
        }

        recurrenceEnabledInput.value = 'true';
        setControlValue(frequencyControl, recurrenceFrequency);
        setControlValue(modifierControl, recurrenceModifier || '');
        setControlValue(anchorControl, recurrenceAnchor || '');

        syncModifierOptions();
        updateStatement();
        setEditingState(false);
        enableSaveButtons([
            'recurrence_anchor',
            'recurrence_enabled',
            'recurrence_frequency',
            'recurrence_modifier',
        ]);
    });

    deleteBtn.addEventListener('click', () => {
        openConfirmationModal(
            'Delete Recurrence?',
            'This action cannot be undone. The task will no longer repeat.',
            async () => {
                recurrenceEnabledInput.value = 'false';
                setControlValue(frequencyControl, '');
                setControlValue(modifierControl, '');
                setControlValue(anchorControl, '');
                updateControl(frequencyControl, { required: false });
                updateControl(modifierControl, {
                    disabled: true,
                    options: [],
                    required: false,
                });
                updateControl(anchorControl, {
                    disabled: true,
                    required: false,
                });
                recurrenceShell.classList.add('hidden');
                setEditingState(false);
                enableSaveButtons([
                    'recurrence_anchor',
                    'recurrence_enabled',
                    'recurrence_frequency',
                    'recurrence_modifier',
                ]);
            },
            true,
            'Delete',
        );
    });

    dueDateRoot.addEventListener('control:valuechange', () => {
        if (warningLock) {return;}

        const currentDueDate = getDueDateValue();

        if ((frequencyControl.value === 'weekly' ||
            frequencyControl.value === 'monthly') &&
            currentDueDate) {
            syncModifierOptions();
        }

        if (currentDueDate || recurrenceEnabledInput.value !== 'true') {
            return;
        }

        warningLock = true;
        const previousDueDate = item?.due_date || '';
        const previousFrequency = frequencyControl.value;
        const previousModifier = modifierControl.value;
        const previousAnchor = anchorControl.value || 'dueDate';

        openConfirmationModal(
            'Stop Recurring Task?',
            'Clearing the due date will stop this task from recurring.',
            async () => {
                recurrenceEnabledInput.value = 'false';
                setControlValue(frequencyControl, '');
                setControlValue(modifierControl, '');
                setControlValue(anchorControl, '');
                updateControl(frequencyControl, { required: false });
                updateControl(modifierControl, {
                    disabled: true,
                    options: [],
                    required: false,
                });
                updateControl(anchorControl, {
                    disabled: true,
                    required: false,
                });
                recurrenceShell.classList.add('hidden');
                setDueDateValue('');
                enableSaveButtons([
                    'due_date',
                    'recurrence_anchor',
                    'recurrence_enabled',
                    'recurrence_frequency',
                    'recurrence_modifier',
                ]);
                warningLock = false;
            },
            false,
            'Continue',
            () => {
                setDueDateValue(previousDueDate);
                setControlValue(frequencyControl, previousFrequency);
                setControlValue(anchorControl, previousAnchor);
                recurrenceEnabledInput.value = 'true';
                syncModifierOptions();
                const modifierOptions = getTaskDetailRecurrenceModifierOptions(
                    previousFrequency,
                    previousDueDate,
                );
                if (hasOptionValue(modifierOptions, previousModifier)) {
                    setControlValue(modifierControl, previousModifier);
                }
                recurrenceShell.classList.remove('hidden');
                warningLock = false;
            },
            '',
            'Cancel',
        );
    });

    syncModifierOptions();
    updateStatement();
    if (uiDraft.recurrenceShellHidden) {
        recurrenceShell.classList.add('hidden');
    }
    setEditingState(uiDraft.recurrenceEditorOpen === true);
}
